import { createQueryKeys } from "@lukemorales/query-key-factory";

import type { CoreApiClient } from "../client";
import type {
  Building,
  BuildingGraph,
  MapBuilding,
  PlanningsStat,
  Weather,
  WeatherStation,
} from "../types/building.type";

export const createBuildingQueries = (api: CoreApiClient) => {
  const { client, request } = api;

  const list = async (): Promise<MapBuilding[]> => {
    const { data, error } = await client.GET("/buildings");
    if (error) throw new Error(error.message);
    return data;
  };

  const byId = async (buildingId: string): Promise<Building> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const weather = async (buildingId: string): Promise<Weather[]> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/weather`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const planningsStat = async (buildingId: string): Promise<PlanningsStat> => {
    const { data, error } = await client.GET(`/buildings/{buildingId}/plannings-stat`, {
      params: { path: { buildingId } },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const weatherStations = async (buildingId: string): Promise<WeatherStation[]> => {
    const { data } = await request("get", "/buildings/{buildingId}/weather-stations", {
      params: { path: { buildingId } },
    });
    return data;
  };

  const graph = async (buildingId: string): Promise<BuildingGraph> => {
    const { data } = await request("get", "/buildings/{buildingId}/graph", {
      params: { path: { buildingId } },
    });
    return data;
  };

  return {
    list,
    byId,
    weather,
    planningsStat,
    weatherStations,
    graph,
    queries: createQueryKeys("building", {
      all: {
        queryKey: null,
        queryFn: list,
      },
      byId: (buildingId: string) => ({
        queryKey: [buildingId],
        queryFn: () => byId(buildingId),
      }),
      weather: (buildingId: string) => ({
        queryKey: [buildingId, "weather"],
        queryFn: () => weather(buildingId),
      }),
      planningsStat: (buildingId: string) => ({
        queryKey: [buildingId, "plannings-stat"],
        queryFn: () => planningsStat(buildingId),
      }),
      weatherStations: (buildingId: string) => ({
        queryKey: [buildingId, "weather-stations"],
        queryFn: () => weatherStations(buildingId),
      }),
      graph: (buildingId: string) => ({
        queryKey: [buildingId, "graph"],
        queryFn: () => graph(buildingId),
      }),
    }),
  };
};
